import {
  Autocomplete,
  Checkbox,
  Chip,
  ListSubheader,
  TextField,
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useContext, useMemo } from "react";
import { Filters } from "../../models/Filters";
import { useInvoice } from "../../stores/useInvoice";
import { ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { LanguageContext } from "../../globals/LanguageContext";

type QuarterOption = {
  label: string;
  year: string;
  quarter: string;
};

type Props = {
  filters: Filters;
  onChange: (val: Filters) => void;
};

export function QuarterFilter({ filters, onChange }: Props) {
  const { invoices } = useInvoice();
  const { dictionary } = useContext(LanguageContext);

  const allOptions: QuarterOption[] = useMemo(() => {
    const map = new Map<string, Set<string>>();
    invoices &&
      invoices.forEach((inv) => {
        const date = new Date(inv.invoiceDate);
        const year = date.getFullYear().toString();
        const month = date.getMonth() + 1;
        const quarter =
          month <= 3 ? "Q1" : month <= 6 ? "Q2" : month <= 9 ? "Q3" : "Q4";

        if (!map.has(year)) map.set(year, new Set());
        map.get(year)!.add(quarter);
      });

    const result: QuarterOption[] = [];
    Array.from(map.entries())
      .sort((a, b) => parseInt(b[0]) - parseInt(a[0])) // sort by year descending
      .forEach(([year, quarters]) => {
        Array.from(quarters)
          .sort()
          .forEach((q) =>
            result.push({ year, quarter: q, label: `${year} ${q}` })
          );
      });

    return result;
  }, [invoices]);

  const selectedLabels = filters.yearQuarter || [];

  const toggleSortDirection = () => {
    const current = filters.sortDirection?.direction || "asc";
    onChange({
      ...filters,
      sortDirection: {
        key: "quarter",
        direction: current === "asc" ? "desc" : "asc",
      },
    });
  };

  const isYearChecked = (year: string) =>
    allOptions
      .filter((opt) => opt.year === year)
      .every((opt) => selectedLabels.includes(opt.label));

  const isYearIndeterminate = (year: string) => {
    const yearOpts = allOptions.filter((opt) => opt.year === year);
    const selectedCount = yearOpts.filter((opt) =>
      selectedLabels.includes(opt.label)
    ).length;
    return selectedCount > 0 && selectedCount < yearOpts.length;
  };

  const toggleYear = (year: string, checked: boolean) => {
    const yearOpts = allOptions.filter((opt) => opt.year === year);
    const yearLabels = yearOpts.map((opt) => opt.label);
    const updated = checked
      ? [...new Set([...selectedLabels, ...yearLabels])]
      : selectedLabels.filter((label) => !yearLabels.includes(label));
    onChange({ ...filters, yearQuarter: updated });
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" sx={{ mr: 1 }}>
            {dictionary.filters.quarter}
          </Typography>
          <IconButton onClick={toggleSortDirection} size="small">
            {filters.sortDirection?.direction === "desc" ? (
              <ArrowDown fontSize="small" />
            ) : (
              <ArrowUp fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={allOptions}
          disableCloseOnSelect
          groupBy={(option) => option.year}
          getOptionLabel={(option) => option.quarter}
          fullWidth
          value={allOptions.filter((opt) => selectedLabels.includes(opt.label))}
          onChange={(_, newValue) => {
            onChange({
              ...filters,
              yearQuarter: newValue.map((v) => v.label),
            });
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {option.quarter}
            </li>
          )}
          renderGroup={(params) => {
            const year = params.group;
            return [
              <ListSubheader key={year} component="div" sx={{ pl: 1, pr: 1 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isYearChecked(year)}
                    indeterminate={isYearIndeterminate(year)}
                    onChange={(e) => toggleYear(year, e.target.checked)}
                    tabIndex={-1}
                    disableRipple
                  />
                  {year}
                </Box>
              </ListSubheader>,
              params.children,
            ];
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Year & Quarter"
              placeholder="All"
              size="small"
              fullWidth
            />
          )}
          componentsProps={{
            paper: {
              sx: {
                minWidth: "max-content",
                width: "auto",
              },
            },
          }}
        />
      </Grid>
    </Grid>
  );
}
